/**
 * Sidebar contents — DESIGN.md §13.1.
 *
 * "Left sidebar: My Tasks, Inbox (unread badge), Search, Views, Reports,
 * projects list, Admin (admins only). Collapsible; becomes a drawer on
 * mobile."
 *
 * One component renders both forms, because a drawer whose contents have
 * drifted from the pinned sidebar is two things to keep right instead of one.
 * `collapsed` switches to the 3.5rem icon rail; `onNavigate` is how the drawer
 * closes itself. Long-Day Review corrections carried here: the projects list
 * (SHELL-11) and the create/search affordances (SHELL-12, SHELL-20) that used
 * to exist only inside a project or only as an undiscoverable ⌘K.
 */
import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Inbox,
  LayoutGrid,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Shield,
  Star,
} from 'lucide-react';
import type { Project } from '@slate/shared';
import { useAuth, useLogout } from '@/lib/auth';
import { useUnreadCount } from '@/features/activity/api';
import { useRealtime } from '@/lib/realtime';
import { useProjects } from '@/features/projects/api';
import { newCardTarget, useCurrentProjectKey } from '@/lib/current-project';
import { emitUi } from '@/lib/ui-events';
import { Button } from '@/components/ui/button';
import { TimerChip } from '@/components/timer-chip';
import { UserMenu } from '@/components/shell/user-menu';
import { Tooltip, TooltipContent, TooltipKeys, TooltipTrigger } from '@/components/shell/tooltip';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: typeof CheckSquare;
  /**
   * Path prefix that counts as "here". Admin's link points at `/admin/users`
   * but the whole `/admin` tree is Admin, and highlighting on `to` alone lost
   * the marker on every sub-page (SHELL-13).
   */
  match?: string;
  exact?: boolean;
  adminOnly?: boolean;
  /** §13.1 — the Inbox carries an unread count. */
  badge?: 'unread';
}

const NAV: NavItem[] = [
  { to: '/', label: 'My Tasks', icon: CheckSquare, exact: true },
  { to: '/inbox', label: 'Inbox', icon: Inbox, badge: 'unread' },
  { to: '/search', label: 'Search', icon: Search },
  { to: '/views', label: 'Views', icon: LayoutGrid },
  { to: '/projects', label: 'Projects', icon: FolderKanban, exact: true },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/admin/users', label: 'Admin', icon: Shield, match: '/admin', adminOnly: true },
];

function isActive(pathname: string, item: NavItem): boolean {
  const base = item.match ?? item.to;
  if (item.exact) return pathname === base;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** ⌘ on Apple hardware, Ctrl everywhere else — the hint has to be true. */
const IS_APPLE =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || '');
export const MOD_KEY = IS_APPLE ? '⌘' : 'Ctrl+';

export function SidebarNav({
  collapsed,
  onToggleCollapsed,
  onNavigate,
  showTimer,
}: {
  collapsed: boolean;
  /** Absent in the drawer, where collapsing makes no sense. */
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
  /** The mobile header shows the timer itself; the drawer must not repeat it. */
  showTimer: boolean;
}): JSX.Element {
  const projectKey = useCurrentProjectKey();
  const { me, isAdmin } = useAuth();
  const logout = useLogout();
  const unread = useUnreadCount();
  const realtime = useRealtime();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const projects = useProjects('active');

  const items = NAV.filter((item) => !item.adminOnly || isAdmin);
  const unreadCount = unread.data?.unread ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Brand */}
      <div
        className={cn(
          'flex h-14 shrink-0 items-center gap-2 border-b',
          collapsed ? 'justify-center px-1.5' : 'px-4',
        )}
      >
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2 rounded-md font-semibold tracking-tight [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:pr-2"
          aria-label="Slate — My Tasks"
        >
          <span className="bg-primary text-primary-foreground grid size-6 shrink-0 place-items-center rounded text-[0.75rem] font-bold">
            S
          </span>
          {collapsed ? null : 'Slate'}
        </Link>
      </div>

      {/* Actions: the two things you do from anywhere (SHELL-12, SHELL-20). */}
      <div className={cn('flex shrink-0 flex-col gap-1.5 p-2', collapsed && 'px-1.5')}>
        <RailAction
          collapsed={collapsed}
          label="New card"
          keys="C"
          icon={Plus}
          onClick={() => {
            onNavigate?.();
            // Scoped to the project on screen, like the palette and `c` — a
            // card added from here belongs to the list you are looking at.
            emitUi('new-card', newCardTarget(projectKey));
          }}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        />
        <RailAction
          collapsed={collapsed}
          label="Search"
          keys={`${MOD_KEY}K`}
          icon={Search}
          onClick={() => {
            onNavigate?.();
            emitUi('palette', {});
          }}
          className="border bg-transparent"
        />
        {showTimer ? (
          <TimerChip variant={collapsed ? 'rail' : 'full'} className={collapsed ? '' : 'mt-0.5'} />
        ) : null}
      </div>

      {/* Main nav */}
      <nav aria-label="Main" className={cn('shrink-0 px-2 pb-2', collapsed && 'px-1.5')}>
        <ul className="space-y-0.5">
          {items.map((item) => {
            const active = isActive(pathname, item);
            const badge = item.badge === 'unread' && unreadCount > 0 ? unreadCount : null;
            return (
              <li key={item.to}>
                <NavLabelled collapsed={collapsed} label={item.label}>
                  <Link
                    to={item.to}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed ? item.label : undefined}
                    className={cn(
                      'relative flex items-center rounded-md text-sm transition-colors',
                      // §13.6 — 44px rows under a coarse pointer; the rail is
                      // already 2.75rem square.
                      collapsed
                        ? 'size-11 justify-center'
                        : 'gap-2.5 px-3 py-2 [@media(pointer:coarse)]:min-h-[44px]',
                      active
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                    )}
                  >
                    <item.icon className="size-4 shrink-0" aria-hidden="true" />
                    {collapsed ? null : <span className="flex-1 truncate">{item.label}</span>}
                    {badge !== null ? (
                      collapsed ? (
                        <span
                          className="bg-primary absolute right-1 top-1 size-2 rounded-full"
                          aria-label={`${badge} unread`}
                        />
                      ) : (
                        <span className="bg-primary text-primary-foreground shrink-0 rounded-full px-1.5 py-0.5 text-[0.75rem] font-semibold leading-none">
                          {badge}
                        </span>
                      )
                    ) : null}
                  </Link>
                </NavLabelled>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Projects — §13.1's "projects list"; followed ones first (SHELL-11). */}
      <ProjectList
        collapsed={collapsed}
        pathname={pathname}
        onNavigate={onNavigate}
        projects={projects.data?.items ?? []}
      />

      <div className={cn('shrink-0 border-t p-2', collapsed && 'px-1.5')}>
        {/* §15 — only the *unhealthy* state is worth a permanent line (SHELL-18). */}
        {!realtime.connected ? (
          <p
            className="text-muted-foreground px-1 pb-1.5 text-[0.75rem]"
            role="status"
            title="Live updates reconnecting"
          >
            {collapsed ? '···' : 'Live updates reconnecting…'}
          </p>
        ) : null}

        {onToggleCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-sidebar-toggle=""
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!collapsed}
                onClick={onToggleCollapsed}
                className={cn(
                  'text-muted-foreground mb-1',
                  collapsed ? 'mx-auto flex' : 'ml-auto flex',
                )}
              >
                {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              <TooltipKeys>[</TooltipKeys>
            </TooltipContent>
          </Tooltip>
        ) : null}

        {me ? (
          <UserMenu
            user={me}
            name={me.name}
            email={me.email}
            collapsed={collapsed}
            onNavigate={onNavigate}
            signOutPending={logout.isPending}
            onSignOut={() => logout.mutate()}
          />
        ) : null}
      </div>
    </div>
  );
}

/** Wraps a rail item in a tooltip; expanded items already show their label. */
function NavLabelled({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: string;
  children: JSX.Element;
}): JSX.Element {
  if (!collapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function RailAction({
  collapsed,
  label,
  keys,
  icon: Icon,
  onClick,
  className,
}: {
  collapsed: boolean;
  label: string;
  keys: string;
  icon: typeof Plus;
  onClick: () => void;
  className?: string;
}): JSX.Element {
  const button = (
    <button
      type="button"
      onClick={onClick}
      aria-label={collapsed ? `${label} (${keys})` : undefined}
      className={cn(
        'focus-visible:ring-ring flex items-center rounded-md text-sm font-medium transition-colors',
        collapsed ? 'size-11 justify-center self-center' : 'w-full gap-2.5 px-3 py-2',
        className,
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      {collapsed ? null : (
        <>
          <span className="flex-1 truncate text-left">{label}</span>
          <kbd className="text-muted-foreground shrink-0 font-mono text-[0.75rem] leading-none opacity-80">
            {keys}
          </kbd>
        </>
      )}
    </button>
  );

  if (!collapsed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>
        {label}
        <TooltipKeys>{keys}</TooltipKeys>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The projects list. Followed projects sort first — §6.12's follow *is* the
 * "favourite" signal the review asked for, so there is no second concept to
 * teach. The list scrolls; everything above and below it does not.
 */
function ProjectList({
  collapsed,
  pathname,
  projects,
  onNavigate,
}: {
  collapsed: boolean;
  pathname: string;
  projects: Project[];
  onNavigate?: () => void;
}): JSX.Element {
  const sorted = [...projects].sort((a, b) => {
    if (a.isFollowing !== b.isFollowing) return a.isFollowing ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  /*
   * #26 — following only becomes a filter once someone has actually followed
   * something. With nothing followed the list is every project, because a
   * sidebar that hides all of them behind a disclosure teaches nobody that
   * following exists.
   *
   * Not applied to the collapsed rail: it is icons, so the others cost almost
   * nothing there, and a "7 more" row in a 44px column reads as a glyph.
   */
  const followed = sorted.filter((project) => project.isFollowing);
  const others = sorted.filter((project) => !project.isFollowing);
  const grouped = !collapsed && followed.length > 0 && others.length > 0;
  const [showOthers, setShowOthers] = useState(false);
  const visible = grouped && !showOthers ? followed : sorted;

  return (
    <div className={cn('min-h-0 flex-1 overflow-y-auto px-2 pb-2', collapsed && 'px-1.5')}>
      {collapsed ? (
        <div className="bg-border mx-auto mb-2 h-px w-6" />
      ) : (
        <h2 className="text-muted-foreground px-3 pb-1 pt-1 text-[0.75rem] font-semibold uppercase tracking-wide">
          Projects
        </h2>
      )}
      {sorted.length === 0 ? (
        collapsed ? null : (
          <p className="text-muted-foreground px-3 py-1 text-xs">No projects yet.</p>
        )
      ) : (
        <ul className="space-y-0.5">
          {visible.map((project) => {
            const active = pathname === `/projects/${project.key}`;
            const label = `${project.key} — ${project.name}`;
            return (
              <li key={project.id}>
                <NavLabelled collapsed={collapsed} label={label}>
                  <Link
                    to="/projects/$key"
                    params={{ key: project.key }}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed ? label : undefined}
                    className={cn(
                      'flex items-center rounded-md text-sm transition-colors',
                      collapsed
                        ? 'size-11 justify-center'
                        : 'gap-2 px-3 py-1.5 [@media(pointer:coarse)]:min-h-[44px]',
                      active
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'grid shrink-0 place-items-center rounded font-mono text-[0.75rem] font-semibold leading-none',
                        collapsed ? 'size-7' : 'size-5',
                      )}
                      style={{ backgroundColor: `${project.color}33`, color: project.color }}
                    >
                      {project.key.slice(0, 2)}
                    </span>
                    {collapsed ? null : (
                      <>
                        <span className="min-w-0 flex-1 truncate">{project.name}</span>
                        {project.isFollowing ? (
                          <Star
                            className="size-3 shrink-0 fill-current opacity-60"
                            aria-label="Following"
                          />
                        ) : null}
                      </>
                    )}
                  </Link>
                </NavLabelled>
              </li>
            );
          })}
        </ul>
      )}
      {grouped ? (
        <button
          type="button"
          onClick={() => setShowOthers((current) => !current)}
          aria-expanded={showOthers}
          className="text-muted-foreground hover:bg-accent/60 hover:text-foreground mt-0.5 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs [@media(pointer:coarse)]:min-h-[44px]"
        >
          {showOthers ? (
            <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0" aria-hidden="true" />
          )}
          <span className="min-w-0 flex-1 truncate text-left">
            {showOthers ? 'Hide' : 'Show'} {others.length} more
          </span>
        </button>
      ) : null}
    </div>
  );
}
